import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api from "../Utils/Axios";
import * as XLSX from "xlsx";

function ExpenseLedger() {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [company, setCompany] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const printRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toLocaleDateString('en-CA'),
    travellingExpense: '',
    refreshment: '',
    cargo: '',
    salaryExpense: '',
    paymentMethod: '',
    description: '',
    descriptionUrdu: ''
  });
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchCompanyData();
    fetchExpenses();
    fetchPaymentMethods();

    // Set default date range (current month)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setDateRange({
      startDate: firstDay.toLocaleDateString('en-CA'),
      endDate: lastDay.toLocaleDateString('en-CA')
    });
  }, []);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        navigate("/company/login");
        return;
      }
      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const companyData = response.data.company;
      setCompany(companyData);
      const lang = companyData?.websiteLanguage || companyData?.language || 'en';
      setCompanyLanguage(lang);
    } catch (error) {
      console.error("Fetch company error:", error);
    }
  };

  const fetchExpenses = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("companyToken");
      const response = await api.get("/expense-ledger/company/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const expensesData = response.data.expenses || [];
      console.log("Fetched expenses:", expensesData);
      console.log("First expense paymentMethod:", expensesData[0]?.paymentMethod);
      setExpenses(expensesData);
    } catch (error) {
      console.error("Fetch expenses error:", error);
      toast.error(companyLanguage === 'ur' ? 'اخراجات لوڈ کرنے میں ناکام' : "Failed to load expenses");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get("/payment-method/active");
      const allMethods = response.data.paymentMethods || [];

      // Show ALL payment methods (نقد رقم, Easypaisa, Banks, etc.)
      // All methods that admin added in payment methods
      setPaymentMethods(allMethods);

      console.log("Payment methods fetched:", allMethods.length);
    } catch (error) {
      console.error("Fetch payment methods error:", error);
      toast.error(companyLanguage === 'ur' ? 'ادائیگی کے طریقے لوڈ کرنے میں ناکام' : "Failed to load payment methods");
      // Fallback to default methods if API fails
      setPaymentMethods([
        { _id: '1', name: 'نقد رقم', nameUrdu: 'نقد رقم' },
        { _id: '2', name: 'Easypaisa', nameUrdu: 'ایزی پیسہ' },
        { _id: '3', name: 'Punjab Bank', nameUrdu: 'پنجاب بینک' }
      ]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Expense') || name === 'cargo' ? value.replace(/[^0-9.]/g, '') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("companyToken");
      const payload = {
        date: formData.date,
        travellingExpense: parseFloat(formData.travellingExpense) || 0,
        refreshment: parseFloat(formData.refreshment) || 0,
        cargo: parseFloat(formData.cargo) || 0,
        salaryExpense: parseFloat(formData.salaryExpense) || 0,
        paymentMethod: formData.paymentMethod || '',
        description: formData.description,
        descriptionUrdu: formData.descriptionUrdu
      };

      if (editingExpense) {
        await api.put(`/expense-ledger/company/${editingExpense._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(companyLanguage === 'ur' ? 'اخراجات اپڈیٹ ہو گئے' : "Expense updated successfully");
      } else {
        await api.post("/expense-ledger/company/create", payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(companyLanguage === 'ur' ? 'اخراجات شامل ہو گئے' : "Expense added successfully");
      }

      setShowAddModal(false);
      setEditingExpense(null);
      resetForm();
      fetchExpenses();
    } catch (error) {
      console.error("Save expense error:", error);
      toast.error(companyLanguage === 'ur' ? 'اخراجات محفوظ کرنے میں ناکام' : "Failed to save expense");
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      date: new Date(expense.date).toLocaleDateString('en-CA'),
      travellingExpense: expense.travellingExpense || '',
      refreshment: expense.refreshment || '',
      cargo: expense.cargo || '',
      salaryExpense: expense.salaryExpense || '',
      paymentMethod: expense.paymentMethod || '',
      description: expense.description || '',
      descriptionUrdu: expense.descriptionUrdu || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm(companyLanguage === 'ur' ? 'کیا آپ واقعی یہ اندراج حذف کرنا چاہتے ہیں؟' : 'Are you sure you want to delete this entry?')) {
      return;
    }
    try {
      const token = localStorage.getItem("companyToken");
      await api.delete(`/expense-ledger/company/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(companyLanguage === 'ur' ? 'اندراج حذف ہو گیا' : "Entry deleted successfully");
      fetchExpenses();
    } catch (error) {
      console.error("Delete expense error:", error);
      toast.error(companyLanguage === 'ur' ? 'اندراج حذف کرنے میں ناکام' : "Failed to delete entry");
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toLocaleDateString('en-CA'),
      travellingExpense: '',
      refreshment: '',
      cargo: '',
      salaryExpense: '',
      paymentMethod: '',
      description: '',
      descriptionUrdu: ''
    });
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    if (!printRef.current) {
      toast.error(isUrdu ? 'مواد نہیں ملا' : 'Content not found');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info(isUrdu ? 'PDF بنایا جا رہا ہے...' : 'Generating PDF...');

      const element = printRef.current;

      // Show print header
      const printHeader = element.querySelector('.pdf-print-header');
      if (printHeader) {
        printHeader.style.display = 'block';
      }

      // Hide elements that should be hidden in print
      const hiddenElements = element.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
      hiddenElements.forEach(el => {
        el.style.display = 'none';
      });

      // Add temporary styles for PDF
      const style = document.createElement('style');
      style.id = 'pdf-print-styles';
      style.textContent = `
        .bg-gradient-to-br { background: white !important; }
        .bg-brand-background { background: white !important; }
        .bg-white { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0.5cm !important; }
        .rounded-xl, .rounded-lg { border-radius: 0 !important; }
        .shadow-lg, .shadow-2xl { box-shadow: none !important; }
        table { width: 100% !important; max-width: 100% !important; table-layout: auto !important; }
        .overflow-x-auto { width: 100% !important; overflow: visible !important; }
        div[ref] { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
      `;
      document.head.appendChild(style);

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      // Restore original display states
      if (printHeader) {
        printHeader.style.display = '';
      }
      hiddenElements.forEach(el => {
        el.style.display = '';
      });

      // Remove temporary styles
      const tempStyle = document.getElementById('pdf-print-styles');
      if (tempStyle) {
        document.head.removeChild(tempStyle);
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `Expense_Ledger_${new Date().toLocaleDateString('en-CA')}.pdf`;

      pdf.save(fileName);
      toast.success(isUrdu ? 'PDF ڈاؤن لوڈ ہو گیا!' : 'PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(isUrdu ? 'PDF بنانے میں ناکام' : 'Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Print function
  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = () => {
    try {
      // Prepare data for Excel
      const excelData = expenses.map(expense => ({
        [companyLanguage === 'ur' ? 'تاریخ' : 'Date']: new Date(expense.date).toLocaleDateString('en-GB'),
        [companyLanguage === 'ur' ? 'سفر کے اخراجات' : 'Travelling Expense']: expense.travellingExpense || 0,
        [companyLanguage === 'ur' ? 'تازگی' : 'Refreshment']: expense.refreshment || 0,
        [companyLanguage === 'ur' ? 'کارگو' : 'Cargo']: expense.cargo || 0,
        [companyLanguage === 'ur' ? 'تنخواہ کے اخراجات' : 'Salary Expense']: expense.salaryExpense || 0,
        [companyLanguage === 'ur' ? 'ادائیگی کا طریقہ' : 'Payment Method']: expense.paymentMethod || '',
        [companyLanguage === 'ur' ? 'کل' : 'Total']: (expense.travellingExpense || 0) + (expense.refreshment || 0) + (expense.cargo || 0) + (expense.salaryExpense || 0),
        [companyLanguage === 'ur' ? 'تفصیل' : 'Description']: expense.descriptionUrdu || expense.description || ''
      }));

      // Add total row
      const totals = {
        [companyLanguage === 'ur' ? 'تاریخ' : 'Date']: companyLanguage === 'ur' ? 'کل' : 'Total',
        [companyLanguage === 'ur' ? 'سفر کے اخراجات' : 'Travelling Expense']: expenses.reduce((sum, e) => sum + (e.travellingExpense || 0), 0),
        [companyLanguage === 'ur' ? 'تازگی' : 'Refreshment']: expenses.reduce((sum, e) => sum + (e.refreshment || 0), 0),
        [companyLanguage === 'ur' ? 'کارگو' : 'Cargo']: expenses.reduce((sum, e) => sum + (e.cargo || 0), 0),
        [companyLanguage === 'ur' ? 'تنخواہ کے اخراجات' : 'Salary Expense']: expenses.reduce((sum, e) => sum + (e.salaryExpense || 0), 0),
        [companyLanguage === 'ur' ? 'ادائیگی کا طریقہ' : 'Payment Method']: '',
        [companyLanguage === 'ur' ? 'کل' : 'Total']: expenses.reduce((sum, e) => sum + (e.travellingExpense || 0) + (e.refreshment || 0) + (e.cargo || 0) + (e.salaryExpense || 0), 0),
        [companyLanguage === 'ur' ? 'تفصیل' : 'Description']: ''
      };

      excelData.push(totals);

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, companyLanguage === 'ur' ? 'اخراجات' : 'Expenses');

      // Generate filename with current date
      const filename = `Expense-Ledger-${new Date().toLocaleDateString('en-CA')}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      toast.success(companyLanguage === 'ur' ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : "Excel file downloaded successfully");
    } catch (error) {
      console.error("Export Excel error:", error);
      toast.error(companyLanguage === 'ur' ? 'Excel فائل بنانے میں ناکام' : "Failed to export Excel file");
    }
  };

  const calculateTotal = (field) => {
    return expenses
      .filter(expense => {
        if (!dateRange.startDate && !dateRange.endDate) return true;
        const expenseDate = new Date(expense.date);
        if (dateRange.startDate && expenseDate < new Date(dateRange.startDate)) return false;
        if (dateRange.endDate) {
          const endDate = new Date(dateRange.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (expenseDate > endDate) return false;
        }
        return true;
      })
      .reduce((sum, expense) => sum + (expense[field] || 0), 0);
  };

  const grandTotal = calculateTotal('travellingExpense') + calculateTotal('refreshment') + calculateTotal('cargo') + calculateTotal('salaryExpense');

  // Filter expenses by date range for display
  const filteredExpenses = expenses.filter(expense => {
    if (!dateRange.startDate && !dateRange.endDate) return true;
    const expenseDate = new Date(expense.date);
    if (dateRange.startDate && expenseDate < new Date(dateRange.startDate)) return false;
    if (dateRange.endDate) {
      const endDate = new Date(dateRange.endDate);
      endDate.setHours(23, 59, 59, 999);
      if (expenseDate > endDate) return false;
    }
    return true;
  });

  const isUrdu = companyLanguage === 'ur';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
          <p className={`mt-4 text-gray-600 ${isUrdu ? 'font-urdu' : ''}`}>
            {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'کمپنی اخراجات کھاتہ' : 'Company Expense Ledger'}</title>
      </Helmet>
      <div className={`min-h-screen bg-brand-background py-8 px-4 ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-brand-card animate-fade-in-up">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className={`text-3xl font-bold text-brand-header mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                  {isUrdu ? 'کمپنی اخراجات کھاتہ' : 'Company Expense Ledger'}
                </h1>
                <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`}>
                  {isUrdu ? 'اپنے تمام اخراجات کو منظم کریں' : 'Manage all your expenses'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {isGeneratingPDF ? (isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...') : (isUrdu ? 'PDF ڈاؤن لوڈ' : 'Download PDF')}
                </button>
                <button
                  onClick={handleExportExcel}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {isUrdu ? 'Excel ڈاؤن لوڈ' : 'Download Excel'}
                </button>
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  {isUrdu ? 'پرنٹ' : 'Print'}
                </button>
                <button
                  onClick={() => {
                    resetForm();
                    setEditingExpense(null);
                    setShowAddModal(true);
                  }}
                  className="px-4 py-2 bg-brand-header hover:bg-emerald-800 text-white font-semibold rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {isUrdu ? 'نیا اندراج' : 'Add Entry'}
                </button>
              </div>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-brand-card">
            <div className={`flex items-center gap-4 flex-wrap ${isUrdu ? 'flex-row-reverse' : ''}`}>
              <div className="flex-1 min-w-[200px]">
                <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                  {isUrdu ? 'شروع کی تاریخ:' : 'Start Date:'}
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                  {isUrdu ? 'اختتام کی تاریخ:' : 'End Date:'}
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent"
                />
              </div>
              <div className="flex items-end gap-3">
                <button
                  onClick={() => setDateRange({ startDate: '', endDate: '' })}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-2 rounded-lg transition-all transform hover:scale-105"
                >
                  {isUrdu ? 'کلیر' : 'Clear'}
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid md:grid-cols-5 gap-4 mb-6 animate-fade-in-up">
            <div className="bg-white rounded-lg shadow-lg p-4 border border-brand-card hover:shadow-xl transition-all transform hover:-translate-y-1">
              <p className={`text-sm text-gray-600 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                {isUrdu ? 'سفر کے اخراجات' : 'Travelling'}
              </p>
              <p className="text-2xl font-bold text-brand-header">
                {calculateTotal('travellingExpense').toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-4 border border-brand-card hover:shadow-xl transition-all transform hover:-translate-y-1">
              <p className={`text-sm text-gray-600 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                {isUrdu ? 'تازگی' : 'Refreshment'}
              </p>
              <p className="text-2xl font-bold text-brand-header">
                {calculateTotal('refreshment').toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-4 border border-brand-card hover:shadow-xl transition-all transform hover:-translate-y-1">
              <p className={`text-sm text-gray-600 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                {isUrdu ? 'کارگو' : 'Cargo'}
              </p>
              <p className="text-2xl font-bold text-brand-header">
                {calculateTotal('cargo').toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-4 border border-brand-card hover:shadow-xl transition-all transform hover:-translate-y-1">
              <p className={`text-sm text-gray-600 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                {isUrdu ? 'تنخواہ' : 'Salary'}
              </p>
              <p className="text-2xl font-bold text-brand-header">
                {calculateTotal('salaryExpense').toLocaleString()}
              </p>
            </div>
            <div className="bg-gradient-to-br from-brand-header to-emerald-800 rounded-lg shadow-lg p-4 text-white hover:shadow-xl transition-all transform hover:-translate-y-1">
              <p className={`text-sm mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                {isUrdu ? 'کل اخراجات' : 'Grand Total'}
              </p>
              <p className="text-2xl font-bold">
                {grandTotal.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Expenses Table */}
          <div ref={printRef} className="bg-white rounded-lg shadow-lg border border-brand-card animate-fade-in-up">
            {/* Print Header - Company Info */}
            {company && (
              <div className="hidden print:block border-b-2 border-gray-400 pb-3 mb-4 bg-white p-4 pdf-print-header">
                <div className="flex items-start justify-between mb-2 gap-4">
                  {/* Company Name - Top Left */}
                  <div className="text-left flex-shrink-0">
                    <h1
                      className={`text-xl font-bold ${isUrdu ? 'font-urdu' : ''}`}
                      style={{
                        ...(isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}),
                        color: '#1e40af',
                        WebkitPrintColorAdjust: 'exact',
                        printColorAdjust: 'exact'
                      }}
                    >
                      {company?.shopName || company?.username || company?.shopNameUrdu || 'Company Name'}
                    </h1>
                    {company?.username && (
                      <p className={`text-sm text-gray-600 font-medium mt-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                        {isUrdu ? 'نام:' : 'Name:'} {company.username}
                      </p>
                    )}
                    {(company?.contactNumber || company?.phone || company?.mobile) && (
                      <p className="text-sm text-gray-700 font-semibold mt-1">
                        {isUrdu ? 'فون:' : 'Phone:'} {company.contactNumber || company.phone || company.mobile}
                      </p>
                    )}
                  </div>

                  {/* Logo - Top Right */}
                  <div className="flex-shrink-0" style={{ alignSelf: 'flex-start' }}>
                    {company?.logo && (
                      <img
                        src={company.logo}
                        alt="Logo"
                        className="h-16 w-16 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </div>
                {company?.address && (
                  <div className="mt-2 pt-2 border-t border-gray-300">
                    <p className={`text-xs text-gray-700 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                      {company.address}
                    </p>
                  </div>
                )}
                {/* Title */}
                <div className="mt-3 pt-2">
                  <h2 className={`text-lg font-bold text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                    {isUrdu ? 'کمپنی اخراجات کھاتہ' : 'Company Expense Ledger'}
                  </h2>
                  {(dateRange.startDate || dateRange.endDate) && (
                    <p className={`text-sm text-gray-600 text-center mt-1 ${isUrdu ? 'font-urdu' : ''}`}>
                      {dateRange.startDate && dateRange.endDate
                        ? `${isUrdu ? 'تاریخ:' : 'Date:'} ${new Date(dateRange.startDate).toLocaleDateString('en-GB')} ${isUrdu ? 'سے' : 'to'} ${new Date(dateRange.endDate).toLocaleDateString('en-GB')}`
                        : dateRange.startDate
                          ? `${isUrdu ? 'شروع:' : 'From:'} ${new Date(dateRange.startDate).toLocaleDateString('en-GB')}`
                          : dateRange.endDate
                            ? `${isUrdu ? 'تک:' : 'To:'} ${new Date(dateRange.endDate).toLocaleDateString('en-GB')}`
                            : ''}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="p-4 border-b border-brand-card print:hidden">
              <h3 className={`text-lg font-semibold text-brand-header ${isUrdu ? 'font-urdu' : ''}`}>
                {isUrdu ? 'اخراجات' : 'Expenses'}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-background">
                  <tr>
                    <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تاریخ' : 'Date'}
                    </th>
                    <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'سفر کے اخراجات' : 'Travelling'}
                    </th>
                    <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تازگی' : 'Refreshment'}
                    </th>
                    <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'کارگو' : 'Cargo'}
                    </th>
                    <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تنخواہ' : 'Salary'}
                    </th>
                    <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'ادائیگی کا طریقہ' : 'Payment Method'}
                    </th>
                    <th className={`px-4 py-3 text-right font-semibold text-brand-header border-b border-brand-card ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'کل' : 'Total'}
                    </th>
                    <th className={`px-4 py-3 text-center font-semibold text-brand-header border-b border-brand-card print:hidden ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'عمل' : 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.length === 0 ? (
                    <tr>
                      <td colSpan="8" className={`px-4 py-8 text-center text-gray-500 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'ابھی کوئی اندراج نہیں' : 'No entries yet'}
                      </td>
                    </tr>
                  ) : (
                    filteredExpenses.map((expense, index) => {
                      const total = (expense.travellingExpense || 0) + (expense.refreshment || 0) + (expense.cargo || 0) + (expense.salaryExpense || 0);
                      return (
                        <tr key={expense._id} className={`border-b border-brand-card hover:bg-brand-background transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <td className={`px-4 py-3 text-right ${isUrdu ? 'font-urdu' : ''}`}>
                            {new Date(expense.date).toLocaleDateString('en-GB')}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {(expense.travellingExpense || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {(expense.refreshment || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {(expense.cargo || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {(expense.salaryExpense || 0).toLocaleString()}
                          </td>
                          <td className={`px-4 py-3 text-right ${isUrdu ? 'font-urdu' : ''}`}>
                            {expense.paymentMethod ? (
                              <span className={isUrdu ? 'font-urdu' : ''}>
                                {expense.paymentMethod}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-brand-header">
                            {total.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 print:hidden">
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => handleEdit(expense)}
                                className="px-3 py-1 bg-brand-button hover:bg-yellow-400 text-gray-900 text-sm rounded transition-all transform hover:scale-105"
                              >
                                {isUrdu ? 'تبدیل' : 'Edit'}
                              </button>
                              <button
                                onClick={() => handleDelete(expense._id)}
                                className="px-3 py-1 bg-brand-alert hover:bg-red-600 text-white text-sm rounded transition-all transform hover:scale-105"
                              >
                                {isUrdu ? 'حذف' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {expenses.length > 0 && (
                  <tfoot className="bg-brand-background">
                    <tr>
                      <td className={`px-4 py-3 text-right font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'کل' : 'Total'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-brand-header">
                        {calculateTotal('travellingExpense').toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-brand-header">
                        {calculateTotal('refreshment').toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-brand-header">
                        {calculateTotal('cargo').toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-brand-header">
                        {calculateTotal('salaryExpense').toLocaleString()}
                      </td>
                      <td></td>
                      <td className="px-4 py-3 text-right font-bold text-brand-header text-lg">
                        {grandTotal.toLocaleString()}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in-up">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-brand-card">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-2xl font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`}>
                    {editingExpense ? (isUrdu ? 'اندراج تبدیل کریں' : 'Edit Entry') : (isUrdu ? 'نیا اندراج' : 'Add Entry')}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingExpense(null);
                      resetForm();
                    }}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'تاریخ' : 'Date'} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'سفر کے اخراجات' : 'Travelling Expense'}
                      </label>
                      <input
                        type="text"
                        name="travellingExpense"
                        value={formData.travellingExpense}
                        onChange={handleInputChange}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      />
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'تازگی' : 'Refreshment'}
                      </label>
                      <input
                        type="text"
                        name="refreshment"
                        value={formData.refreshment}
                        onChange={handleInputChange}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      />
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'کارگو' : 'Cargo'}
                      </label>
                      <input
                        type="text"
                        name="cargo"
                        value={formData.cargo}
                        onChange={handleInputChange}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      />
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'تنخواہ کے اخراجات' : 'Salary Expense'}
                      </label>
                      <input
                        type="text"
                        name="salaryExpense"
                        value={formData.salaryExpense}
                        onChange={handleInputChange}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                      />
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'ادائیگی کا طریقہ' : 'Payment Method'}
                      </label>
                      <select
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all ${isUrdu ? 'font-urdu' : ''}`}
                      >
                        <option value="">{isUrdu ? 'منتخب کریں' : 'Select'}</option>
                        {paymentMethods.map((method) => (
                          <option key={method._id} value={method.nameUrdu || method.name}>
                            {method.nameUrdu || method.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تفصیل (انگریزی)' : 'Description (English)'}
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="2"
                      className="w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all"
                    />
                  </div>

                  <div className="animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تفصیل (اردو)' : 'Description (Urdu)'}
                    </label>
                    <textarea
                      name="descriptionUrdu"
                      value={formData.descriptionUrdu}
                      onChange={handleInputChange}
                      rows="2"
                      className={`w-full px-3 py-2 border border-brand-card rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent transition-all ${isUrdu ? 'font-urdu' : ''}`}
                      style={{ fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif' }}
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        setEditingExpense(null);
                        resetForm();
                      }}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-all transform hover:scale-105"
                    >
                      {isUrdu ? 'منسوخ' : 'Cancel'}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-brand-header hover:bg-emerald-800 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-lg"
                    >
                      {editingExpense ? (isUrdu ? 'اپڈیٹ کریں' : 'Update') : (isUrdu ? 'محفوظ کریں' : 'Save')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            margin: 0.5cm;
          }
          .bg-gradient-to-r, .bg-gradient-to-br {
            background: #059669 !important;
          }
          .bg-brand-background {
            background: white !important;
          }
          .bg-white {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0.5cm !important;
          }
          .rounded-xl, .rounded-lg {
            border-radius: 0 !important;
          }
          .shadow-lg, .shadow-2xl {
            box-shadow: none !important;
          }
          table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: auto !important;
          }
          .overflow-x-auto {
            width: 100% !important;
            overflow: visible !important;
          }
          div[ref] {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </>
  );
}

export default ExpenseLedger;
